import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Edit, Search } from 'lucide-react';

// Inventory item from API response
interface InventoryItemFromAPI {
  _id: string;
  ingredientId: {
    _id: string;
    name: string;
    sku: string;
    category: string;
    unit: string;
    minStockLevel?: number;
    isComposite?: boolean;
  };
  branchId: string;
  quantity: number;
  unit: string;
  expiryDate?: string;
  costPerUnit?: number;
}

interface InventoryTableProps {
  items: InventoryItemFromAPI[];
  onAdjust: (item: InventoryItemFromAPI) => void;
}

function InventoryTable({ items, onAdjust }: InventoryTableProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');

  // Get unique categories
  const categories = [...new Set(items.map(item => item.ingredientId?.category).filter(Boolean))];

  // Filter items by search query and category
  const filteredItems = items.filter((item) => {
    const name = item.ingredientId?.name || '';
    const matchesSearch = name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !categoryFilter || item.ingredientId?.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const getStockStatus = (item: InventoryItemFromAPI) => {
    const minStock = item.ingredientId?.minStockLevel || 0;
    if (item.quantity <= 0) {
      return { label: t('inventory.outOfStock') || 'Out of Stock', color: 'text-red-600 bg-red-50' };
    }
    if (item.quantity <= minStock) {
      return { label: t('inventory.lowStock') || 'Low Stock', color: 'text-orange-600 bg-orange-50' };
    }
    if (item.quantity >= minStock * 5) {
      return { label: t('inventory.overstock') || 'Overstock', color: 'text-blue-600 bg-blue-50' };
    }
    return { label: t('inventory.normal') || 'Normal', color: 'text-green-600 bg-green-50' };
  };

  const getStockPercentage = (item: InventoryItemFromAPI) => {
    const minStock = item.ingredientId?.minStockLevel || 100;
    const maxStock = minStock * 5;
    if (maxStock <= 0) return 0;
    return Math.min(100, (item.quantity / maxStock) * 100);
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('inventory.searchIngredients') || 'Search ingredients...'}
            className="w-full rounded-lg border bg-background pl-10 pr-4 py-2"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="rounded-lg border bg-background px-4 py-2 min-w-[160px]"
        >
          <option value="">All Categories</option>
          {categories.sort().map((cat) => (
            <option key={cat} value={cat}>
              {cat.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </option>
          ))}
        </select>
      </div>

      {/* Summary */}
      <div className="text-sm text-muted-foreground">
        Showing {filteredItems.length} of {items.length} items
      </div>

      {/* Table */}
      <div className="rounded-lg border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="text-left p-3 font-medium">{t('inventory.ingredient') || 'Ingredient'}</th>
                <th className="text-left p-3 font-medium">Category</th>
                <th className="text-right p-3 font-medium">{t('inventory.currentStock') || 'Current Stock'}</th>
                <th className="text-right p-3 font-medium">Min Level</th>
                <th className="text-center p-3 font-medium">{t('inventory.status') || 'Status'}</th>
                <th className="text-center p-3 font-medium">{t('common.actions') || 'Actions'}</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center p-8 text-muted-foreground">
                    {searchQuery || categoryFilter ? 'No results found' : 'No inventory items'}
                  </td>
                </tr>
              ) : (
                filteredItems.map((item) => {
                  const status = getStockStatus(item);
                  const percentage = getStockPercentage(item);
                  const minStock = item.ingredientId?.minStockLevel || 0;

                  return (
                    <tr key={item._id} className="border-t hover:bg-muted/50">
                      <td className="p-3">
                        <div className="font-medium">{item.ingredientId?.name || 'Unknown'}</div>
                        <div className="text-xs text-muted-foreground">{item.ingredientId?.sku}</div>
                        {item.quantity <= minStock && minStock > 0 && (
                          <div className="flex items-center gap-1 text-xs text-orange-600 mt-1">
                            <AlertTriangle className="h-3 w-3" />
                            <span>Needs reorder</span>
                          </div>
                        )}
                      </td>
                      <td className="p-3">
                        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs bg-muted">
                          {item.ingredientId?.category?.replace(/-/g, ' ')}
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        <div className="font-bold">{item.quantity.toLocaleString()}</div>
                        <div className="text-xs text-muted-foreground">{item.unit}</div>
                        {/* Stock level bar */}
                        <div className="w-20 bg-gray-200 rounded-full h-1.5 mt-2 ml-auto">
                          <div
                            className={`h-1.5 rounded-full ${
                              percentage > 80 ? 'bg-blue-500' :
                              percentage > 30 ? 'bg-green-500' :
                              percentage > 10 ? 'bg-orange-500' :
                              'bg-red-500'
                            }`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </td>
                      <td className="p-3 text-right">
                        <div>{minStock.toLocaleString()}</div>
                        <div className="text-xs text-muted-foreground">{item.unit}</div>
                      </td>
                      <td className="p-3 text-center">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${status.color}`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <button
                          onClick={() => onAdjust(item)}
                          className="inline-flex items-center gap-1 rounded-lg border bg-background px-3 py-1.5 text-sm hover:bg-secondary transition-colors tap-highlight-none"
                        >
                          <Edit className="h-4 w-4" />
                          {t('inventory.adjust') || 'Adjust'}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default InventoryTable;
